import React, { useState, useEffect } from 'react';
import AIGaragePage from './components/AIGarage/AIGaragePage';
import GarageLogin from './components/AIGarage/GarageLogin';
import config from './config/environment';
import authService from './services/authService';
import { normalizeUserEmail } from './utils/userEmail';
import './App.css';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const handleLogout = () => {
      setUser(null);
      setLoading(false);
    };

    window.addEventListener('aigarage:logout', handleLogout);
    return () => window.removeEventListener('aigarage:logout', handleLogout);
  }, []);

  // Handle initial URL routing - ensure we're on a valid AI Garage route
  useEffect(() => {
    const path = window.location.pathname;

    // If we're at root path, redirect to AI Garage discover
    if (path === '/') {
      console.log('🔄 Root path detected, redirecting to /ai-garage/discover');
      window.history.replaceState({}, '', '/ai-garage/discover');
    }
  }, []);

  useEffect(() => {
    // Check for SSO errors first
    try {
      const ssoError = sessionStorage.getItem('aigarage_sso_error');
      if (ssoError) {
        sessionStorage.removeItem('aigarage_sso_error');
        console.error('❌ SSO Login Failed:', ssoError);
        alert(`SSO Login Failed: ${ssoError}`);
        setLoading(false);
        return;
      }
    } catch (error) {
      console.error('Failed to read SSO error from sessionStorage:', error);
    }

    authService.purgeStaleSession();

    if (config.DEV_MODE) {
      const devUser = authService.getDevelopmentUserInfo();
      console.log('🔧 DEV_MODE: Using mock user:', devUser.email);
      setUser({
        id: 1,
        email: devUser.email,
        name: devUser.name,
        role: 'user',
      });
      setLoading(false);
    } else {
      const checkAuth = async () => {
        const authMeUrl = '/api/v1/auth/me';

        try {
          const token = authService.getToken();

          if (!token) {
            console.log('🔒 No authentication token, showing sign-in screen');
            setUser(null);
            setLoading(false);
            return;
          }

          const response = await fetch(authMeUrl, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            credentials: 'include',
          });

          if (response.ok) {
            const userData = await response.json();
            const normalized = {
              ...userData,
              email: normalizeUserEmail(userData.email || userData.sub),
            };
            console.log('✅ User authenticated:', normalized.email);
            localStorage.setItem('aigarage_user_info', JSON.stringify({
              email: normalized.email,
              name: normalized.name,
              authMethod: normalized.auth_method || 'saml',
            }));
            setUser(normalized);
            setLoading(false);
          } else {
            console.log('🔒 Token invalid, showing sign-in screen');
            authService.removeToken();
            setUser(null);
            setLoading(false);
          }
        } catch (error) {
          console.error('Auth check failed:', error);
          setUser(null);
          setLoading(false);
        }
      };

      checkAuth();
    }
  }, []);

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '18px',
        color: '#666'
      }}>
        Loading AI Garage...
      </div>
    );
  }

  if (!config.DEV_MODE && !user) {
    return <GarageLogin />;
  }

  const tokenUser = authService.getUserInfo();
  const resolvedUser = {
    ...tokenUser,
    ...user,
    email: normalizeUserEmail(user?.email || tokenUser?.email),
  };

  return (
    <div className="App">
      <AIGaragePage
        userId={resolvedUser?.id}
        userEmail={resolvedUser?.email}
        user={resolvedUser}
      />
    </div>
  );
}

export default App;
